import { REGEX } from "constants/validate";
import { useEffect, useState } from "react";

export interface ValidateOption {
  required?: {
    message: string;
  };
  email?: {
    message: string;
  };
  sameValue?: {
    value: string;
    message: string;
  };
  minLength?: {
    value: number;
    message: string;
  };
  maxLength?: {
    value: number;
    message: string;
  };
  includeNumber?: {
    message: string;
  };
  includeNumberOnly?: {
    message: string;
  };
  includeAlphabet?: {
    message: string;
  };
  halfWidth?: {
    message: string;
  };
  includeLatin?: {
    message: string;
  };
  includeLatinOnly?: {
    message: string;
  };
  includeNumberOnlyAndStringEmpty?: {
    message: string;
  };
  minNumber?: {
    value: number;
    message?: string;
  };
  maxNumber?: {
    value: number;
    message?: string;
  };
  numericFormat?: {
    precision: number;
    scale: number;
    message?: string;
  };
}

interface ValidateProps extends ValidateOption {
  onChangeText?: (value: string) => void;
  onError?: (error: boolean, message: string) => void;
  clearError?: boolean;
}

/**
 * hook validate for textfield
 * @param props - ValidateProps
 */
export default function useValidateInput(props?: ValidateProps) {
  const { onChangeText, onError, clearError } = props || {};
  const [curentValue, setCurrentValue] = useState("");
  const [state, setState] = useState({
    error: false,
    message: "",
  });

  useEffect(() => {
    if (clearError) {
      setState({ error: false, message: "" });
    }
  }, [clearError]);

  const handleValidate = (value: string) => {
    let error = false;
    let message = "";

    if (props?.required && !value) {
      error = true;
      message = props?.required.message;
    }

    if (!error && props?.email && value && !REGEX.EMAIL.test(value)) {
      error = true;
      message = props?.email.message;
    }

    if (!error && props?.sameValue && value !== props?.sameValue.value) {
      error = true;
      message = props?.sameValue.message;
    }
    if (!error && props?.minLength && value && value.length < props?.minLength.value) {
      error = true;
      message = props?.minLength.message;
    }

    if (!error && props?.maxLength && value && value.length > props?.maxLength.value) {
      error = true;
      message = props?.maxLength.message;
    }

    if (!error && props?.includeNumber && value && !REGEX.INCLUDE_NUMBER.test(value)) {
      error = true;
      message = props?.includeNumber.message;
    }

    if (!error && props?.includeNumberOnly && value && !REGEX.INCLUDE_NUMBER_ONLY.test(value)) {
      error = true;
      message = props?.includeNumberOnly.message;
    }

    if (!error && props?.includeAlphabet && value && !REGEX.INCLUDE_ALPHABET.test(value)) {
      error = true;
      message = props?.includeAlphabet.message;
    }
    if (!error && props?.halfWidth && value && !REGEX.HALFWIDTH.test(value)) {
      error = true;
      message = props?.halfWidth.message;
    }

    if (!error && props?.includeLatin && value && !REGEX.INCLUDE_LATIN.test(value)) {
      error = true;
      message = props?.includeLatin.message;
    }

    if (!error && props?.includeLatinOnly && value && !REGEX.INCLUDE_LATIN_ONLY.test(value)) {
      error = true;
      message = props?.includeLatinOnly.message;
    }

    if (!error && props?.numericFormat) {
      const { precision, scale } = props?.numericFormat ?? { precision: 0, scale: 0 };
      const regex = REGEX.NUMERIC_FORMAT(precision, scale);
      if (value && !regex.test(value)) {
        error = true;
        message = props?.numericFormat.message ?? "";
      }
    }

    if (
      !error &&
      props?.includeNumberOnlyAndStringEmpty &&
      !REGEX.INCLUDE_NUMBER_ONLY_AND_STRING_EMPTY.test(value)
    ) {
      error = true;
      message = props?.includeNumberOnlyAndStringEmpty.message;
    }

    if (
      !error &&
      props?.minNumber &&
      value &&
      (Number.isNaN(value) || Number(value) < props?.minNumber.value)
    ) {
      error = true;
      message = props?.minNumber.message ?? "";
    }

    if (!error && props?.maxNumber && value && Number(value) > props?.maxNumber.value) {
      error = true;
      message = props?.maxNumber.message ?? "";
    }

    onError?.(error, message);
    setState({ error, message });

    return error;
  };

  useEffect(() => {
    if (props?.sameValue?.value) {
      if (props?.sameValue && curentValue !== props?.sameValue.value) {
        onError?.(true, props?.sameValue.message);
        setState({ error: true, message: props?.sameValue.message });
      }
    }
  }, [props?.sameValue?.value]);

  const handleChangeText = (value: string) => {
    const hasErr = handleValidate(value);
    onChangeText?.(value);
    setCurrentValue(value);
    return hasErr;
  };

  return {
    state,
    handleChangeText,
    clearError,
  };
}
